'use client';

import { type FC, type PropsWithChildren, useEffect, useRef, useState } from 'react';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from '@/app/components/ui/tooltip';
import { useDevice } from '@/app/hooks';
import type { RADIO_OPTIONS_SIDE_TYPE } from '@/app/types';
import { cn } from '@/app/utils';

export const CustomTooltip: FC<
  PropsWithChildren<{
    content: React.ReactNode;
    contentClassName?: string;
    arrowClassName?: string;
    delayDuration?: number;
    sideOffset?: number;
    align?: 'start' | 'center' | 'end';
    side?: RADIO_OPTIONS_SIDE_TYPE;
    trigger?: 'hover' | 'click' | 'auto'; // auto 表示自动适配，支持 hover 就用 hover，不支持就用 click
    /** basic: 基础样式; extend: 扩展样式; glass: 毛玻璃样式 */
    variant?: 'basic' | 'extend' | 'glass';
    disableHoverableContent?: boolean;
    onOpenChange?: (open: boolean) => void;
    collisionTop?: number;
    collisionBottom?: number;
    collisionLeft?: number;
    collisionRight?: number;
    collisionBoundary?: HTMLElement | null;
    container?: HTMLElement | null;
    /** showOnlyOnOverflow 是否只在溢出后显示 content */
    showOnlyOnOverflow?: boolean;
    /** 仅在支持 hover 的设备上展示提示；适合触控设备上的图标按钮。 */
    hoverOnly?: boolean;
    maxLines?: number;
    triggerClassName?: string;
    /** 外部控制 tooltip 的显示状态 */
    open?: boolean;
  }>
> = ({
  contentClassName,
  arrowClassName,
  delayDuration = 0,
  content,
  children,
  align,
  side,
  sideOffset,
  trigger = 'auto',
  variant = 'basic',
  disableHoverableContent,
  onOpenChange,
  collisionTop = 24,
  collisionBottom = 24,
  collisionLeft = 16,
  collisionRight = 16,
  collisionBoundary,
  container,
  showOnlyOnOverflow = false,
  hoverOnly = false,
  maxLines,
  triggerClassName,
  open: externalOpen,
}) => {
  const { supportsHover } = useDevice();
  const shouldRenderTooltip = !(hoverOnly && !supportsHover);

  // 根据 trigger 类型决定实际的触发方式
  const effectiveTrigger: 'hover' | 'click' = trigger === 'auto' ? (supportsHover ? 'hover' : 'click') : trigger;

  const [internalOpen, setInternalOpen] = useState<boolean | undefined>(
    effectiveTrigger === 'click' ? false : undefined,
  );
  const childRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // 使用外部传入的 open 状态，如果没有则使用内部状态
  const open = externalOpen !== undefined ? externalOpen : internalOpen;

  useEffect(() => {
    if (!shouldRenderTooltip || (!showOnlyOnOverflow && !maxLines) || !childRef.current) return;

    const checkOverflow = () => {
      const element = childRef.current;
      if (!element) return;

      if (showOnlyOnOverflow) {
        const isTextOverflowing =
          element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;

        setIsOverflowing(isTextOverflowing);
        return;
      }

      if (maxLines) {
        // 确保 element 仍然是有效的 DOM 元素
        if (!element || !element.isConnected) return;

        const lineHeight = Number.parseInt(getComputedStyle(element).lineHeight, 10) || 18;
        const maxHeight = lineHeight * maxLines;
        const isLineOverflowing = element.scrollHeight > maxHeight;

        setIsOverflowing(isLineOverflowing);
      }
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(childRef.current);

    return () => {
      if (childRef.current) {
        resizeObserver.unobserve(childRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [maxLines, shouldRenderTooltip, showOnlyOnOverflow]);

  const handleClickToggle = () => {
    if (effectiveTrigger !== 'click') return;

    const nextOpen = !(open ?? false);

    if (externalOpen === undefined) {
      setInternalOpen(nextOpen);
    } else {
      onOpenChange?.(nextOpen);
    }
  };

  const handleClickOutside = () => {
    if (effectiveTrigger !== 'click') return;

    if (externalOpen === undefined) {
      if (internalOpen) setInternalOpen(false);
    } else {
      if (externalOpen) onOpenChange?.(false);
    }
  };

  const handleTooltipOpenChange = (isOpen: boolean) => {
    if ((showOnlyOnOverflow || maxLines) && !isOverflowing) return;

    if (onOpenChange) {
      onOpenChange(isOpen);
    }

    if (effectiveTrigger === 'click' && externalOpen === undefined) {
      setInternalOpen(isOpen);
    }
  };

  if (!shouldRenderTooltip) return <>{children}</>;

  if (!content) return <>{children}</>;

  const lineClampClass = maxLines ? `line-clamp-${maxLines} break-all text-ellipsis` : '';

  const wrappedChildren = showOnlyOnOverflow ? (
    <span
      ref={childRef}
      className={cn(triggerClassName, maxLines && lineClampClass, isOverflowing && 'group is-overflowing')}
    >
      {children}
    </span>
  ) : (
    children
  );

  if ((showOnlyOnOverflow || maxLines) && !isOverflowing) {
    return wrappedChildren;
  }

  return (
    <TooltipProvider disableHoverableContent={disableHoverableContent}>
      <Tooltip delayDuration={delayDuration} open={open} onOpenChange={handleTooltipOpenChange}>
        <TooltipTrigger asChild onClick={handleClickToggle}>
          {wrappedChildren}
        </TooltipTrigger>
        <TooltipPortal container={container}>
          <TooltipContent
            className={cn(
              'max-w-[380px] whitespace-pre-wrap font-normal sm:mx-8 sm:max-w-[300px]',
              variant === 'basic' && 'py-2.5',
              variant === 'extend' && 'rounded-12',
              variant === 'glass' && 'bg-[rgba(0,0,0,0.4)] px-4 py-2.5 text-sm backdrop-blur-[20px]',
              contentClassName,
            )}
            style={
              variant === 'glass'
                ? {
                    borderRadius: '12px',
                  }
                : undefined
            }
            align={align}
            side={side}
            arrowPadding={8}
            sideOffset={sideOffset}
            onPointerDownOutside={handleClickOutside}
            collisionPadding={{
              left: collisionLeft,
              right: collisionRight,
              top: collisionTop,
              bottom: collisionBottom,
            }}
            collisionBoundary={collisionBoundary}
          >
            {content}
            {variant === 'basic' && <TooltipArrow className={arrowClassName} />}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  );
};
